import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SiteContentProvider } from "@/contexts/SiteContentContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import ProtectedRoute from "@/components/ProtectedRoute";
import PageTransition from "@/components/PageTransition";
import { usePageViewTracker } from "@/hooks/usePageViewTracker";
import { useImageProtection } from "@/hooks/useImageProtection";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Training from "@/pages/Training";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";

// Wraps a single page element with the global transition
const Anim = ({ children }) => <PageTransition>{children}</PageTransition>;

const AnimatedRoutes = () => {
  const location = useLocation();
  usePageViewTracker();
  useImageProtection();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Admin — no transition wrapper (different layout) */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Public site — with transitions */}
        <Route
          path="/"
          element={
            <PublicLayout>
              <Anim>
                <Home />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="/services"
          element={
            <PublicLayout>
              <Anim>
                <Services />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="/services/:slug"
          element={
            <PublicLayout>
              <Anim>
                <ServiceDetail />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="/formation"
          element={
            <PublicLayout>
              <Anim>
                <Training />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="/contact"
          element={
            <PublicLayout>
              <Anim>
                <Contact />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="/a-propos"
          element={
            <PublicLayout>
              <Anim>
                <About />
              </Anim>
            </PublicLayout>
          }
        />
        <Route
          path="*"
          element={
            <PublicLayout>
              <Anim>
                <NotFound />
              </Anim>
            </PublicLayout>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

// Public layout = navbar + page + footer
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
  </>
);

function App() {
  return (
    <div className="App kw-grain" data-testid="app-root">
      <ThemeProvider>
        <AuthProvider>
          <SiteContentProvider>
            <LanguageProvider>
              <BrowserRouter>
                <ScrollToTop />
                <AnimatedRoutes />
                <Toaster
                  position="bottom-right"
                  theme="dark"
                  toastOptions={{
                    style: {
                      background: "#0a0a0a",
                      border: "1px solid rgba(197,160,89,0.35)",
                      color: "#F2EBE5",
                    },
                  }}
                />
              </BrowserRouter>
            </LanguageProvider>
          </SiteContentProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
